import { useEffect, useMemo, useState } from "react";
import type { RefObject } from "react";

export type AspectCorrection = { scaleX: number; scaleY: number };

const IDENTITY: AspectCorrection = { scaleX: 1, scaleY: 1 };

function computeCorrection(w: number, h: number): AspectCorrection {
    if (w <= 0 || h <= 0) return IDENTITY;
    return {
        scaleX: Math.sqrt(h / w),
        scaleY: Math.sqrt(w / h)
    };
}

/**
 * When `active`, applies a non-uniform scale so node/connector math in a square
 * viewBox matches a rectangular target aspect ratio.
 *
 * @param sizeSourceRef When set, width/height come from this element’s
 *   `clientWidth` / `clientHeight` (via `ResizeObserver`). When omitted, uses
 *   `window.innerWidth` / `innerHeight` (legacy).
 */
export function useSpiderwebViewportAspect(
    active: boolean,
    sizeSourceRef?: RefObject<HTMLElement | null>
): AspectCorrection {
    const [dims, setDims] = useState<{ w: number; h: number }>(() =>
        typeof window !== "undefined"
            ? { w: window.innerWidth, h: window.innerHeight }
            : { w: 1, h: 1 }
    );

    useEffect(() => {
        if (!active) {
            return;
        }

        if (!sizeSourceRef) {
            const update = () =>
                setDims({ w: window.innerWidth, h: window.innerHeight });

            update();
            window.addEventListener("resize", update);
            return () => window.removeEventListener("resize", update);
        }

        const measure = () => {
            const el = sizeSourceRef.current;
            if (!el) {
                return;
            }
            const w = el.clientWidth;
            const h = el.clientHeight;
            setDims((prev) => (prev.w === w && prev.h === h ? prev : { w, h }));
        };

        const el = sizeSourceRef.current;
        if (!el) {
            return;
        }

        measure();
        const ro = new ResizeObserver(measure);
        ro.observe(el);

        return () => {
            ro.disconnect();
        };
    }, [active, sizeSourceRef]);

    return useMemo(
        () => (active ? computeCorrection(dims.w, dims.h) : IDENTITY),
        [active, dims.w, dims.h]
    );
}
